import React, { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  MousePointerClick,
  FileText,
  Zap,
  Film,
  Clapperboard,
  Sparkles,
  RotateCcw,
  FastForward,
  Search,
  LineChart,
  LayoutGrid,
} from 'lucide-react';
const WORKFLOW_VIDEO_BASE = "https://anmol-s3-images-2026.s3.ap-south-1.amazonaws.com";
const WORKFLOW_STEP_MS = 6500;

export const PhasesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const videoRef = useRef(null);

  const phases = [
    {
      id: "01",
      icon: MousePointerClick,
      title: "Click-prompt the video",
      desc: "Tap tone, length, style, and where it will play. Those clicks become the production brief.",
      videoUrl: `${WORKFLOW_VIDEO_BASE}/Step1.mov`,
      badge: "CLICK PROMPTING",
      actionText: "START WITH CLICKS",
    },
    {
      id: "02",
      icon: FileText,
      title: "Draft the script",
      desc: "Twinnee turns those clicks into a script, scene beats, and a structure you can lock.",
      videoUrl: `${WORKFLOW_VIDEO_BASE}/Step2.mov`,
      badge: "SCRIPT & SCENES",
      actionText: "LOCK THE SCRIPT",
    },
    {
      id: "03",
      icon: Zap,
      title: "Click the look",
      desc: "Choose visual style, pacing, and frame for YouTube or Shorts before a frame renders.",
      videoUrl: `${WORKFLOW_VIDEO_BASE}/Step3.mov`,
      badge: "STYLE & FORMAT",
      actionText: "SET THE LOOK",
    },
    {
      id: "04",
      icon: Film,
      title: "Produce the video",
      desc: "Voice, scenes, and visuals render into the video you will post to the channel.",
      videoUrl: `${WORKFLOW_VIDEO_BASE}/Step4.mov`,
      badge: "VIDEO PRODUCTION",
      actionText: "PRODUCE VIDEO",
    },
    {
      id: "05",
      icon: Clapperboard,
      title: "Finish the cut",
      desc: "Trim and pace the video in the AI editor so it is ready for a YouTube upload.",
      videoUrl: `${WORKFLOW_VIDEO_BASE}/Step5.mov`,
      badge: "AI EDITOR",
      actionText: "FINISH THE CUT",
    },
    {
      id: "06",
      icon: Sparkles,
      title: "Post with AI autofill",
      desc: "AI writes the YouTube title, description, and tags from the video. Check SEO, then publish.",
      videoUrl: `${WORKFLOW_VIDEO_BASE}/Step6.mov`,
      badge: "YOUTUBE AUTOFILL",
      actionText: "AUTOFILL & POST",
    },
  ];

  const current = phases[activeIndex];

  const goToStep = useCallback((index) => {
    setActiveIndex(index);
    setProgress(0);
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % phases.length);
    setProgress(0);
  }, [phases.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? phases.length - 1 : prev - 1));
    setProgress(0);
  }, [phases.length]);

  useEffect(() => {
    if (progressRef.current) window.clearInterval(progressRef.current);
    setProgress(0);

    const tickMs = 50;
    const increment = (tickMs / WORKFLOW_STEP_MS) * 100;
    progressRef.current = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 100) return 100;
        return p + increment;
      });
    }, tickMs);

    timerRef.current = window.setTimeout(goNext, WORKFLOW_STEP_MS);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (progressRef.current) window.clearInterval(progressRef.current);
    };
  }, [activeIndex, goNext]);

  return (
    <section id="flows" className="text-white py-28 sm:py-32 px-4 sm:px-6 lg:px-8 font-sora relative overflow-hidden">
      <div className="absolute top-[28%] -left-32 w-[550px] h-[550px] rounded-full pointer-events-none z-0" style={{ background: "radial-gradient(circle at center, rgba(208, 188, 255, 0.35) 0%, rgba(208, 188, 255, 0.12) 35%, rgba(208, 188, 255, 0.02) 60%, transparent 70%)", filter: "blur(60px)" }} />
      <div className="absolute top-[15%] -right-24 w-[600px] h-[600px] rounded-full pointer-events-none z-0" style={{ background: "radial-gradient(circle at center, rgba(98, 250, 227, 0.32) 0%, rgba(98, 250, 227, 0.12) 35%, rgba(98, 250, 227, 0.02) 60%, transparent 70%)", filter: "blur(60px)" }} />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-left mb-14 sm:mb-16">
          <p className="text-xs font-semibold tracking-widest text-[#62FAE3] uppercase mb-3">THE WORKFLOW</p>
          <h2 className="text-4xl sm:text-5xl lg:text-[54px] font-bold tracking-tight leading-[1.15] text-white mb-4">
            Idea to publish in <span className="text-[#D0BCFF]">six</span>{" "}<span className="text-[#62FAE3]">phases</span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-base font-light leading-relaxed max-w-3xl">
            Produce the video with click prompting, post it to YouTube with AI autofill, then improve SEO and keywords, run the channel, and watch the performance behind growth and monetization.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          {/* Phase Cards Left Column */}
          <div className="lg:col-span-5 flex flex-col gap-3.5 sm:gap-4">
            {phases.map((p, idx) => {
              const Icon = p.icon;
              const isActive = activeIndex === idx;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => goToStep(idx)}
                  className={`w-full text-left p-4 sm:p-5 rounded-2xl transition-all duration-300 flex flex-col gap-3 cursor-pointer ${
                    isActive
                      ? "bg-white/[0.07] backdrop-blur-2xl border border-[#62FAE3]/50 shadow-[0_0_30px_rgba(98,250,227,0.15)]"
                      : "bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.05] opacity-75 hover:opacity-100"
                  }`}
                >
                  <div className="flex items-start gap-4 w-full">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isActive ? "bg-[#62FAE3]/20 text-[#62FAE3] border border-[#62FAE3]/40" : "bg-white/[0.04] text-gray-400 border border-white/10"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono font-medium tracking-wider text-gray-400 uppercase block mb-0.5">PHASE {p.id}</span>
                        {isActive && (
                          <span className="text-[10px] font-mono font-semibold text-[#62FAE3] bg-[#62FAE3]/10 px-2 py-0.5 rounded-full border border-[#62FAE3]/20">
                            PLAYING
                          </span>
                        )}
                      </div>
                      <h3 className={`text-sm sm:text-base font-semibold tracking-tight ${isActive ? "text-white" : "text-gray-200"}`}>{p.title}</h3>
                      <p className="text-xs text-gray-400 font-light mt-1 leading-relaxed line-clamp-2">{p.desc}</p>
                    </div>
                  </div>

                  {/* Animated Progress Bar on Active Item */}
                  {isActive && (
                    <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mt-1">
                      <div
                        className="h-full bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] transition-[width] duration-75 ease-linear rounded-full"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Studio Video Interface Right Column */}
          <div className="lg:col-span-7 rounded-[28px] border border-white/10 bg-[#0a0d14]/90 backdrop-blur-2xl p-5 sm:p-7 flex flex-col justify-between shadow-2xl relative overflow-hidden min-h-[520px]">
            <div>
              {/* Studio Window Header */}
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                  <span className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                  <span className="w-3 h-3 rounded-full bg-[#27c93f]" />
                </div>
                <span className="text-[11px] font-mono tracking-widest text-gray-400 uppercase">
                  TWINNEE STUDIO • {current.badge}
                </span>
                <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#62FAE3]/10 border border-[#62FAE3]/25 text-[#62FAE3] text-[10px] font-mono font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#62FAE3] animate-pulse" />
                  PHASE {current.id} / 06
                </div>
              </div>

              {/* Video Player Screen with Crossfade Animation */}
              <div className="mt-4 relative w-full aspect-video sm:h-[350px] rounded-2xl overflow-hidden bg-[#04060a] border border-white/10 shadow-inner group">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full"
                  >
                    <video
                      ref={videoRef}
                      src={current.videoUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                      className="w-full h-full object-cover"
                    />

                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/25 pointer-events-none" />

                    {/* Overlay info badge */}
                    <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                      <div>
                        <span className="text-[10px] font-mono font-bold text-[#62FAE3] tracking-widest uppercase">
                          PHASE {current.id}
                        </span>
                        <h4 className="text-base sm:text-lg font-bold text-white tracking-tight drop-shadow-md">
                          {current.title}
                        </h4>
                      </div>
                      <span className="text-[11px] font-mono text-white/80 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-lg border border-white/10">
                        AI STUDIO
                      </span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Dot Indicators */}
              <div className="mt-4 flex items-center justify-center gap-2">
                {phases.map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => goToStep(idx)}
                    className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                      idx === activeIndex
                        ? "w-8 bg-[#62FAE3]"
                        : "w-2 bg-white/20 hover:bg-white/40"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Bottom Controls */}
            <div className="mt-5 pt-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 text-gray-400">
                <div className="flex items-center gap-2">
                  <button
                    onClick={goPrev}
                    title="Previous Phase"
                    className="p-1.5 rounded-lg hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={goNext}
                    title="Next Phase"
                    className="p-1.5 rounded-lg hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
                  >
                    <FastForward className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-xs font-mono text-gray-400">Step {activeIndex + 1} of 6</span>
              </div>
              <button
                onClick={goNext}
                className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] text-[#0a071b] font-bold text-xs tracking-wider transition-all duration-300 hover:brightness-110 shadow-lg shadow-[#62FAE3]/15 active:scale-95 cursor-pointer"
              >
                {current.actionText} →
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          <div className="rounded-[24px] border border-white/10 bg-[#0a0d14]/80 backdrop-blur-xl p-7 shadow-2xl">
            <div className="w-10 h-10 rounded-xl bg-[#62FAE3]/15 border border-[#62FAE3]/30 flex items-center justify-center text-[#62FAE3] mb-4">
              <Search className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold text-[#62FAE3] tracking-tight mb-2">SEO and keywords</h3>
            <p className="text-gray-400 text-xs sm:text-sm font-light leading-relaxed">
              Score the title, description, and tags, then rewrite what is weak. Research phrases from YouTube search, including competition, typical views, and whether this channel already posted them.
            </p>
          </div>
          <div className="rounded-[24px] border border-white/10 bg-[#0a0d14]/80 backdrop-blur-xl p-7 shadow-2xl">
            <div className="w-10 h-10 rounded-xl bg-[#D0BCFF]/15 border border-[#D0BCFF]/30 flex items-center justify-center text-[#D0BCFF] mb-4">
              <LayoutGrid className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold text-white tracking-tight mb-2">Channel management</h3>
            <p className="text-gray-400 text-xs sm:text-sm font-light leading-relaxed">
              Run the connected YouTube channel from one place: videos, SEO scores, competitors, trends, and the next idea to produce.
            </p>
          </div>
          <div className="rounded-[24px] border border-white/10 bg-[#0a0d14]/80 backdrop-blur-xl p-7 shadow-2xl">
            <div className="w-10 h-10 rounded-xl bg-[#3d271d]/60 border border-[#855333]/40 flex items-center justify-center text-[#ff9f68] mb-4">
              <LineChart className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold text-white tracking-tight mb-2">Performance and monetization</h3>
            <p className="text-gray-400 text-xs sm:text-sm font-light leading-relaxed">
              Follow views, watch time, click-through, and subscribers. Those are the signals behind reach and YouTube monetization, and they point to the next video worth making.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhasesSection;
